/**
 * This file exposes frequently used transformers methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import type {
  RvUnifiedSearchResponse,
  SearchPhotosList,
} from '~/types/rental-api-aliases'

/**
 * Swaps the first two photos around and returns the photos.
 *
 * @param rvPhotos The RV photos data.
 * @returns The transformed RV photos data.
 *
 * @example swapPhotos(rvPhotos)
 */
function swapPhotos(rvPhotos: SearchPhotosList) {
  [rvPhotos[0], rvPhotos[1]] = [rvPhotos[1]!, rvPhotos[0]!]

  return rvPhotos
}

/**
 * Transforms the RV results by swapping the photos of the popular RVs if they
 * are also featured.
 *
 * @param data The RVezy API response data.
 * @returns The transformed RVezy API response data.
 *
 * @example transformRVs(data)
 */
export function transformRVs(data: RvUnifiedSearchResponse) {
  if (data.PopularRVs?.ListRVs?.length && data.FeaturedRVs?.ListRVs?.length) {
    const featuredRVsIds = new Set(data.FeaturedRVs.ListRVs.map((rv) => rv.Id))

    data.PopularRVs.ListRVs.forEach((popularRV) => {
      if (featuredRVsIds.has(popularRV.Id) && popularRV.Photos && popularRV.Photos.length > 1) {
        popularRV.Photos = swapPhotos(popularRV.Photos)
      }
    })
  }

  return data
}

/**
 * Transforms the RVezy API response to only include the Featured RVs.
 *
 * @param data The RVezy API response data.
 * @returns The transformed RVezy API response data.
 *
 * @example transformFeaturedRVs(data)
 */
export function transformFeaturedRVs(data: RvUnifiedSearchResponse) {
  return data.FeaturedRVs
}

/**
 * Transforms the RVezy API response to only include the Popular RVs.
 *
 * @param data The RVezy API response data.
 * @returns The transformed RVezy API response data.
 *
 * @example transformPopularRVs(data)
 */
export function transformPopularRVs(data: RvUnifiedSearchResponse) {
  return data.PopularRVs
}
